import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, ArrowRight, Lock, Star, TrendingUp, Zap, AlertTriangle, Lightbulb, X,
} from "lucide-react";

// ---------- Industry Loophole map (Write-Off Predictor) ----------
const LOOPHOLE_TIPS = {
  meals: { title: "50% Meals Loophole", body: "Client meals are 50% deductible — split the tab in your bookkeeping to unlock ~$X/mo.", tag: "IRS §274" },
  office_supplies: { title: "De Minimis Safe Harbor", body: "Items under $2,500 can be expensed immediately, no depreciation schedule needed.", tag: "IRS §1.263(a)-1" },
  electronics: { title: "Section 179 Instant Expense", body: "Business laptops & monitors qualify for full first-year deduction up to $1.16M.", tag: "IRS §179" },
  transportation: { title: "Mileage Method vs Actual", body: "You can pick whichever gives the bigger write-off — check both annually.", tag: "IRS Pub 463" },
  travel: { title: "Away-From-Home Rule", body: "Lodging, transit & 100% of business-day meals deductible if trip > 1 workday.", tag: "IRS §162" },
  software: { title: "SaaS = Ordinary & Necessary", body: "Subscriptions are fully deductible in the year paid — no capitalization needed.", tag: "IRS §162" },
  entertainment: { title: "2018+ Entertainment Rule", body: "Pure entertainment is non-deductible, but sports/concert client-facing meals can qualify.", tag: "TCJA §274(a)" },
  groceries: { title: "Home Office Snacks", body: "If you run a client-facing home office, break-room supplies are 100% deductible.", tag: "IRS §162" },
  other: { title: "Ordinary & Necessary Test", body: "Any expense that's common in your industry AND helpful can potentially be a write-off.", tag: "IRS §162(a)" },
};

export function WriteOffPredictor({ stats, receipts, user }) {
  // Lock only when session is explicitly free_tier OR user has no paid tier at all.
  // Dev-override tokens ("pro_tier") and live "active" both unlock the widget.
  const isFreeTier =
    !user?.subscription_tier || user?.subscription_status === "free_tier";
  const topCat = stats
    ? Object.entries(stats.by_category || {}).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1])[0]
    : null;
  const catKey = topCat ? topCat[0] : "other";
  const tip = LOOPHOLE_TIPS[catKey] || LOOPHOLE_TIPS.other;
  const catSpend = topCat ? topCat[1] : 0;
  const potentialSave = Math.round((catSpend || 0) * 0.24); // 24% effective bracket estimate
  const [dismissed, setDismissed] = useState(false);
  if (dismissed || !stats) return null;
  return (
    <div className="mb-6 fade-up" data-testid="writeoff-predictor">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="glass rounded-2xl p-5 relative overflow-hidden border"
        style={{
          borderColor: "rgba(16,185,129,0.28)",
          background: "linear-gradient(135deg, rgba(16,185,129,0.08) 0%, rgba(15,23,42,0.4) 100%)",
        }}
      >
        <div
          className="absolute -right-16 -top-16 w-56 h-56 rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(16,185,129,0.35), transparent 60%)", filter: "blur(20px)" }}
        />
        <div
          className="relative flex items-start gap-4"
          style={isFreeTier ? { filter: "blur(6px)", opacity: 0.55, pointerEvents: "none", userSelect: "none" } : undefined}
          aria-hidden={isFreeTier || undefined}
        >
          <div className="w-11 h-11 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-300 shrink-0">
            <Lightbulb size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-emerald-400 font-mono text-[10px] uppercase tracking-[0.3em]">
              Industry Loophole · {tip.tag}
            </div>
            <div className="text-white font-bold text-lg tracking-tight mt-1" data-testid="writeoff-title">
              {tip.title}
            </div>
            <div className="text-slate-300 text-sm mt-1 leading-relaxed">{tip.body}</div>
            {potentialSave > 0 && (
              <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/25">
                <Zap size={11} className="text-emerald-300" />
                <span className="font-mono text-xs text-emerald-200">
                  ~${potentialSave.toLocaleString()} potential shielded / yr
                </span>
              </div>
            )}
          </div>
          {!isFreeTier && (
            <button
              onClick={() => setDismissed(true)}
              data-testid="writeoff-dismiss"
              className="text-slate-500 hover:text-white p-1 -m-1 shrink-0"
              aria-label="Dismiss loophole"
            >
              <X size={14} />
            </button>
          )}
        </div>
        {isFreeTier && (
          <div className="absolute inset-0 flex items-center justify-center" data-testid="writeoff-lock-overlay">
            <div className="flex flex-col items-center gap-2 px-5 py-3 rounded-xl bg-slate-950/70 border border-emerald-500/30 backdrop-blur-sm">
              <div className="flex items-center gap-2 text-emerald-300">
                <Lock size={14} />
                <span className="font-mono text-[10px] uppercase tracking-[0.28em]">Pro Feature</span>
              </div>
              <div className="text-white font-semibold text-sm text-center leading-tight">AI Corporate Expense Write-Off Predictor</div>
              <a
                href="/billing"
                data-testid="writeoff-upgrade-cta"
                className="mt-1 text-[10px] font-mono uppercase tracking-widest text-emerald-300 hover:text-emerald-200"
              >
                Upgrade to Pro →
              </a>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}

// ---------- Tax Savings Meter (charitable-donation write-off tracker) ----------
export function TaxSavingsMeter() {
  const [data, setData] = useState(null);
  useEffect(() => {
    const load = () => api.get("/insights/donations").then((r) => setData(r.data)).catch(() => setData({ count: 0, total_donations_usd: 0, estimated_tax_savings: 0 }));
    load();
    const onRefresh = () => load();
    window.addEventListener("receipts-refresh", onRefresh);
    return () => window.removeEventListener("receipts-refresh", onRefresh);
  }, []);
  if (!data || data.count === 0) return null;
  const factor = Math.round((data.writeoff_factor || 0.25) * 100);
  const cap = Math.max(500, Math.ceil(data.estimated_tax_savings / 500) * 500);
  const pct = Math.min(100, Math.round((data.estimated_tax_savings / cap) * 100));
  return (
    <div
      className="mb-6 rounded-2xl p-5 relative overflow-hidden border"
      data-testid="tax-savings-meter"
      style={{
        borderColor: "rgba(16,185,129,0.28)",
        background: "linear-gradient(135deg, rgba(16,185,129,0.08) 0%, rgba(15,23,42,0.4) 100%)",
      }}
    >
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <div className="text-emerald-300 font-mono text-[10px] uppercase tracking-[0.28em]">
            Tax Savings Meter · Donations Ledger
          </div>
          <div className="mt-1 text-slate-400 text-xs">
            {data.count} donation{data.count !== 1 ? "s" : ""} logged · ${data.total_donations_usd.toLocaleString("en-US", { minimumFractionDigits: 2 })} contributed
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-mono text-3xl font-bold text-emerald-300" data-testid="tax-savings-total">
            ${data.estimated_tax_savings.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">
            estimated write-off · {factor}%
          </div>
        </div>
      </div>
      <div className="mt-4 h-2 rounded-full bg-white/5 overflow-hidden border border-white/5">
        <div
          className="h-full rounded-full"
          style={{
            width: `${pct}%`,
            background: "linear-gradient(90deg, #10B981, #34d399)",
            boxShadow: "0 0 20px rgba(16,185,129,0.5)",
            transition: "width 800ms cubic-bezier(0.22,1,0.36,1)",
          }}
        />
      </div>
    </div>
  );
}

// ---------- Priority Queue Mirror (business-tier ops shortcut, polled) ----------
export function PriorityQueueMirror({ user, healthPulse = false }) {
  const [data, setData] = useState(null);
  const [pulse, setPulse] = useState(false);
  const prevCountRef = useRef(0);
  const navigate = useNavigate();
  useEffect(() => {
    const isBiz = user?.subscription_tier === "business";
    if (!isBiz) return;
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const { data: d } = await api.get("/admin/leads/cpa-consult");
        if (cancelled) return;
        const newCount = d?.business_high_risk_count ?? (d?.priority_queue?.length || 0);
        if (newCount > prevCountRef.current && prevCountRef.current !== 0) {
          setPulse(true);
          setTimeout(() => setPulse(false), 4200);
        }
        prevCountRef.current = newCount;
        setData(d);
      } catch { /* keep last snapshot */ }
    };
    fetchOnce();
    const iv = setInterval(fetchOnce, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user]);
  if (!data || !data.priority_queue?.length) return null;
  const count = data.business_high_risk_count || data.priority_queue.length;
  // Dollarized deal-value projection:
  //   High-risk business leads → $4,500 avg CPA annual retainer, 25% close-rate
  //   Moderate-risk leads      → $1,800 avg CPA annual retainer, 25% close-rate
  const AVG_HIGH = 4500, AVG_MOD = 1800, CLOSE_RATE = 0.25;
  const dealValue = Math.round(
    data.priority_queue.reduce((sum, l) =>
      sum + (l.risk_label === "High" ? AVG_HIGH : AVG_MOD) * CLOSE_RATE, 0)
  );
  const topThree = data.priority_queue.slice(0, 3).map((l) => l.name || l.email?.split("@")[0]).filter(Boolean).join(", ");
  return (
    <button
      onClick={() => navigate("/enterprise?tab=cpa_leads")}
      data-testid="priority-queue-mirror"
      className={`w-full mb-6 group flex items-center gap-4 px-4 sm:px-5 py-3 min-h-[56px] rounded-xl border transition-all hover:translate-x-0.5 ${(pulse || healthPulse) ? "mirror-pulse-emerald" : ""}`}
      style={{
        borderColor: "rgba(245,158,11,0.35)",
        background: "linear-gradient(90deg, rgba(245,158,11,0.10) 0%, rgba(15,23,42,0.4) 60%)",
      }}
    >
      <div className="w-9 h-9 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-300 shrink-0">
        <Star size={14} />
      </div>
      <div className="flex-1 min-w-0 text-left">
        <div className="text-amber-300 font-mono text-[10px] uppercase tracking-[0.28em]">
          Priority Queue · Partner Rev-Share {pulse && <span className="ml-2 text-emerald-300">◆ NEW</span>}
        </div>
        <div className="text-white text-sm sm:text-base font-semibold tracking-tight truncate" data-testid="priority-queue-headline-dollar">
          ${dealValue.toLocaleString("en-US")} lifetime deal-value
          <span className="text-slate-400 font-normal"> across {count} hot lead{count !== 1 ? "s" : ""}</span>
        </div>
        {topThree && (
          <div className="text-slate-500 text-[11px] font-mono truncate mt-0.5 hidden sm:block">
            {topThree}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 text-amber-300 text-xs font-mono uppercase tracking-widest shrink-0 group-hover:gap-2 transition-all">
        Open <ArrowRight size={13} />
      </div>
    </button>
  );
}

// ---------- Cash Advance Mirror (business-tier signals shortcut, polled) ----------
export function CashAdvanceMirror({ user, receipts }) {
  const [data, setData] = useState(null);
  const [pulse, setPulse] = useState(false);
  const prevDollarRef = useRef(0);
  const navigate = useNavigate();
  useEffect(() => {
    const isBiz = user?.subscription_tier === "business";
    if (!isBiz) return;
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const { data: d } = await api.get("/admin/capital-advance/metrics");
        if (cancelled) return;
        const newDollar = Number(d?.total_requested_dollar || 0);
        if (newDollar > prevDollarRef.current && prevDollarRef.current !== 0) {
          setPulse(true);
          setTimeout(() => setPulse(false), 4200);
        }
        prevDollarRef.current = newDollar;
        setData(d);
      } catch { /* keep last snapshot */ }
    };
    fetchOnce();
    const iv = setInterval(fetchOnce, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user]);
  const totalRequested = Number(data?.total_requested_dollar || 0);
  const bigReceipt = (receipts || []).find((r) => Number(r.total || 0) >= 1000);
  const threshold = totalRequested >= 1000 || !!bigReceipt;
  if (!threshold) return null;
  const headline = totalRequested >= 1000
    ? `$${totalRequested.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} in advance signals — unlock now`
    : `$${Number(bigReceipt.total).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} receipt eligible for instant advance`;
  return (
    <button
      onClick={() => navigate("/enterprise?tab=signals")}
      data-testid="cash-advance-mirror"
      className={`w-full mb-6 group flex items-center gap-4 px-5 py-3 rounded-xl border transition-all hover:translate-x-0.5 ${pulse ? "mirror-pulse-emerald" : ""}`}
      style={{
        borderColor: "rgba(16,185,129,0.35)",
        background: "linear-gradient(90deg, rgba(16,185,129,0.12) 0%, rgba(15,23,42,0.4) 60%)",
      }}
    >
      <div className="w-9 h-9 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-300 shrink-0">
        <Zap size={14} />
      </div>
      <div className="flex-1 min-w-0 text-left">
        <div className="text-emerald-300 font-mono text-[10px] uppercase tracking-[0.28em]">
          Cash-Flow Advance {pulse && <span className="ml-2 text-emerald-200">◆ NEW</span>}
        </div>
        <div className="text-white text-sm font-semibold tracking-tight truncate">{headline}</div>
      </div>
      <div className="flex items-center gap-1 text-emerald-300 text-xs font-mono uppercase tracking-widest shrink-0 group-hover:gap-2 transition-all">
        Route <ArrowRight size={13} />
      </div>
    </button>
  );
}

